import { useState, useEffect } from 'react';
import SEOHead from '../../components/base/SEOHead';
import Navbar from '../../components/feature/Navbar';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface ReferralData {
  id: string;
  referred_user: {
    full_name: string;
    email: string;
  };
  reward_amount: number;
  status: string;
  created_at: string;
  completed_at?: string;
}

interface ReferralStats {
  totalReferrals: number;
  completedReferrals: number;
  totalEarnings: number;
  pendingRewards: number;
}

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  email: string;
  phone: string;
}

export default function ReferralPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState<ReferralData[]>([]);
  const [stats, setStats] = useState<ReferralStats>({
    totalReferrals: 0,
    completedReferrals: 0,
    totalEarnings: 0,
    pendingRewards: 0
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    email: '',
    phone: ''
  });
  const [searchEmail, setSearchEmail] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showLostReferralsModal, setShowLostReferralsModal] = useState(false);
  const [lostReferrals, setLostReferrals] = useState<any[]>([]);
  const [isClaimingReferral, setIsClaimingReferral] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [claimingUserId, setClaimingUserId] = useState<string | null>(null);

  const currentDomain = window.location.origin;
  const referralLink = `${currentDomain}/signup?ref=${referralCode}`;

  const generateInstantReferralCode = () => {
    try {
      const cachedCode = localStorage.getItem('userReferralCode');
      if (cachedCode) {
        setReferralCode(cachedCode);
        return cachedCode;
      }

      const timestamp = Date.now().toString().slice(-6);
      const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
      const tempCode = `PEP${timestamp}${randomStr}`;
      
      setReferralCode(tempCode);
      return tempCode;
    } catch (error) {
      console.error('Error generating instant code:', error);
      const fallbackCode = `PEP${Date.now().toString().slice(-8)}`;
      setReferralCode(fallbackCode);
      return fallbackCode;
    }
  };

  const generateNewCode = async () => {
    try {
      setGenerating(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        setError('Please sign in to generate a referral code');
        setGenerating(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session expired. Please sign in again.');
        setGenerating(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/referral-system-v5`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            action: 'generate_referral_code'
          }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to generate code: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.referralCode) {
        setReferralCode(result.referralCode);
        localStorage.setItem('userReferralCode', result.referralCode);
        
        const successMsg = document.createElement('div');
        successMsg.className = 'fixed top-4 right-4 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        successMsg.innerHTML = '<i class="ri-check-line"></i><span>New referral code generated!</span>';
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);
      } else {
        throw new Error(result.error || 'Failed to generate code');
      }
    } catch (error: any) {
      console.error('Error generating new code:', error);
      if (error.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError('Failed to generate new code. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    generateInstantReferralCode();
    loadReferralData();
    loadNotificationSettings();
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      setUser(currentUser);

      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .single();

      if (profileData) {
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const handleRefreshPage = async () => {
    setLoading(true);
    try {
      await loadReferralData();
      await loadUserProfile();
      
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      successMsg.innerHTML = '<i class="ri-check-line"></i><span>Page refreshed successfully!</span>';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
    } catch (error) {
      console.error('Error refreshing:', error);
      alert('Failed to refresh. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReferralData = async () => {
    try {
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        const guestCode = `GUEST${Date.now().toString().slice(-6)}`;
        setReferralCode(guestCode);
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const fallbackCode = `USER${Date.now().toString().slice(-6)}`;
        setReferralCode(fallbackCode);
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/referral-system-v5`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              action: 'get_referral_stats'
            }),
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          const realCode = result.referralCode;
          if (realCode && realCode !== 'LOADING...') {
            setReferralCode(realCode);
            localStorage.setItem('userReferralCode', realCode);
          }
          setStats(result.stats || stats);
          setReferrals(result.referrals || []);
        } else {
          console.warn('API returned error:', result.error);
        }
      } catch (fetchError: any) {
        if (fetchError.name === 'AbortError') {
          console.warn('API call timed out, using instant code');
        } else {
          console.warn('API call failed:', fetchError);
        }
      }
    } catch (error) {
      console.error('Error in loadReferralData:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationSettings = () => {
    const saved = localStorage.getItem('referralNotifications');
    if (saved) {
      setNotifications(JSON.parse(saved));
    }
  };

  const saveNotificationSettings = () => {
    localStorage.setItem('referralNotifications', JSON.stringify(notifications));
    setShowNotificationSettings(false);
    alert('Notification settings saved successfully!');
  };

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } else {
            alert(`Copy this link: ${referralLink}`);
          }
        } catch (err) {
          alert(`Copy this link: ${referralLink}`);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert(`Copy this link: ${referralLink}`);
    }
  };

  const shareOnSocial = (platform: string) => {
    const text = `Join PepCoin and start earning crypto! Use my referral code: ${referralCode}`;
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + referralLink)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`
    };
    
    window.open(urls[platform as keyof typeof urls], '_blank', 'width=600,height=400');
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(referralLink);
    const text = encodeURIComponent(`Join me on PepCoin and start earning! Use my referral code: ${referralCode}`);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      completed: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      pending: 'bg-amber-100 text-amber-700 border border-amber-200',
      failed: 'bg-red-100 text-red-700 border border-red-200'
    };
    
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const shareOnTwitter = () => {
    const text = `Join me on PepCoin and start earning! Use my referral code: ${referralCode}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnTikTok = () => {
    const text = `Join me on PepCoin and start earning! 🚀💰 Use my referral code: ${referralCode}`;
    const shareText = `${text}\n\n${referralLink}\n\n#PepCoin #Crypto #Referral #Earnings`;
    
    navigator.clipboard.writeText(shareText).then(() => {
      const message = document.createElement('div');
      message.className = 'fixed top-4 right-4 bg-cyan-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
      message.innerHTML = `
        <div class="flex items-center gap-2">
          <i class="ri-check-line text-xl"></i>
          <span>Copied! Open TikTok app to share</span>
        </div>
      `;
      document.body.appendChild(message);
      
      setTimeout(() => {
        message.remove();
      }, 3000);
      
      setTimeout(() => {
        window.location.href = 'tiktok://';
      }, 500);
    }).catch(() => {
      alert('Please copy your referral link manually to share on TikTok');
    });
  };

  const shareOnWhatsApp = () => {
    const text = `Join me on PepCoin and start earning! 🚀💰 Use my referral code: ${referralCode}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + referralLink)}`;
    window.open(whatsappUrl, '_blank', 'width=600,height=400');
  };

  const shareOnTelegram = () => {
    const text = `Join me on PepCoin and start earning! 🚀💰 Use my referral code: ${referralCode}`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`;
    window.open(telegramUrl, '_blank', 'width=600,height=400');
  };

  const searchLostReferrals = async () => {
    if (!searchEmail.trim()) {
      alert('Please enter an email address to search');
      return;
    }

    setSearchLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Session expired. Please sign in again.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/referral-system-v5`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            action: 'search_all_users',
            searchEmail: searchEmail.trim()
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setLostReferrals(result.potentialReferrals || []);
        
        if (result.potentialReferrals && result.potentialReferrals.length === 0) {
          alert('No users found with this email address or all found users are already claimed.');
        }
      } else {
        throw new Error(result.error || 'Search failed');
      }
    } catch (error) {
      console.error('Error searching lost referrals:', error);
      alert('Failed to search for lost referrals. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClaimLostReferral = async (referredUserId: string) => {
    setIsClaimingReferral(referredUserId);
    
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        alert('❌ Please sign in to claim referrals');
        setIsClaimingReferral(null);
        return;
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/referral-system-v5`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session.access_token}`
          },
          body: JSON.stringify({
            action: 'claim_lost_referral',
            referredUserId: referredUserId
          }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Claim failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Show success message with wallet balance info
        const successMessage = result.walletUpdated 
          ? `✅ Success! $6 added to your wallet.\n\n💰 New Balance: $${result.newBalance}\n\n📍 Go to your Wallet page to see the updated balance!`
          : '✅ Referral claimed! $6 has been credited.\n\n📍 Please refresh your Wallet page to see the updated balance.';
        
        alert(successMessage);
        
        // Refresh referral data
        loadReferralData();
        
        // Remove claimed user from lost referrals list
        setLostReferrals(prev => prev.filter(r => r.id !== referredUserId));
        
        // Clear search results
        setSearchResults([]);
        setSearchEmail('');
      } else {
        throw new Error(result.error || 'Failed to claim referral');
      }
    } catch (error: any) {
      console.error('Error claiming referral:', error);
      
      if (error.name === 'AbortError') {
        alert('❌ Request timed out. Please try again.');
      } else {
        alert(`❌ ${error.message || 'Failed to claim referral. Please try again.'}`);
      }
    } finally {
      setIsClaimingReferral(null);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);

      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      // Upload new avatar
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update local state
      setProfile({ ...profile, avatar_url: publicUrl });
      alert('Profile picture updated successfully!');
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      alert(`Failed to upload picture: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query.trim() || !user) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const searchTerm = query.toLowerCase().trim();

      // First, get all users already referred by current user
      const { data: existingReferrals } = await supabase
        .from('referrals')
        .select('referred_user_id')
        .eq('referrer_id', user.id);

      const referredIds = new Set(existingReferrals?.map(r => r.referred_user_id) || []);

      // Search in profiles table
      const { data: profileMatches, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, full_name, created_at')
        .or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`)
        .neq('id', user.id)
        .limit(50);

      if (profileError) throw profileError;

      // Filter out already referred users from profile matches
      const availableProfiles = (profileMatches || []).filter(p => !referredIds.has(p.id));

      // Search in auth.users for email matches (only if we need more results)
      let emailMatches: any[] = [];
      if (availableProfiles.length < 15) {
        const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
        
        if (!authError && authUsers) {
          emailMatches = authUsers.users
            .filter(u => 
              u.id !== user.id && 
              !referredIds.has(u.id) &&
              u.email?.toLowerCase().includes(searchTerm)
            )
            .map(u => ({
              id: u.id,
              email: u.email,
              full_name: u.user_metadata?.full_name || u.email?.split('@')[0] || 'Unknown',
              created_at: u.created_at
            }));
        }
      }

      // Combine and deduplicate results
      const allMatches = [...availableProfiles, ...emailMatches];
      const uniqueMatches = Array.from(
        new Map(allMatches.map(item => [item.id, item])).values()
      );

      // All results are claimable since we already filtered out referred users
      const resultsWithStatus = uniqueMatches.map(u => ({
        ...u,
        isReferred: false,
        canClaim: true
      }));

      // Sort by relevance
      resultsWithStatus.sort((a, b) => {
        const aEmailMatch = a.email?.toLowerCase() === searchTerm;
        const bEmailMatch = b.email?.toLowerCase() === searchTerm;
        if (aEmailMatch && !bEmailMatch) return -1;
        if (!aEmailMatch && bEmailMatch) return 1;
        
        const aNameMatch = a.full_name?.toLowerCase() === searchTerm;
        const bNameMatch = b.full_name?.toLowerCase() === searchTerm;
        if (aNameMatch && !bNameMatch) return -1;
        if (!aNameMatch && bNameMatch) return 1;
        
        return 0;
      });

      // Limit to 15 results
      setSearchResults(resultsWithStatus.slice(0, 15));
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClaimReferral = async (referredUserId: string) => {
    if (!user || claimingUserId) return;

    setClaimingUserId(referredUserId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('❌ Please sign in to claim referrals');
        setClaimingUserId(null);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/referral-system-v5`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            action: 'claim_referral',
            referrerId: user.id,
            referredUserId: referredUserId
          }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Claim failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Show success message with wallet balance info
        const successMessage = result.walletUpdated 
          ? `✅ Success! $6 added to your wallet.\n\n💰 New Balance: $${result.newBalance}\n\n📍 Go to your Wallet page to see the updated balance!`
          : '✅ Referral claimed! $6 has been credited.\n\n📍 Please refresh your Wallet page to see the updated balance.';
        
        alert(successMessage);
        
        // Refresh all data
        loadReferralData();
        
        // Update search results to mark this user as referred
        setSearchResults(prev => prev.map(r => 
          r.id === referredUserId 
            ? { ...r, isReferred: true, canClaim: false }
            : r
        ));
      } else {
        throw new Error(result.error || 'Failed to claim referral');
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        alert('⏱️ Request timed out. Please try again.');
      } else {
        console.error('Claim error:', error);
        alert(`❌ ${error.message || 'Failed to claim referral. Please try again.'}`);
      }
    } finally {
      setClaimingUserId(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <>
      <SEOHead 
        title="Referral Program - Earn PEP Tokens | PepVerse"
        description="Invite friends to PepVerse and earn PEP tokens for every successful referral. Share your unique referral code and build your crypto earnings."
        keywords="referral program, earn crypto, PEP tokens, invite friends, crypto rewards"
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Navbar />
        
        <div className="pt-20 sm:pt-24 pb-8 sm:pb-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/62fa766e17c813a1758bb17dba938cac.jpg')] bg-cover bg-center opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/50 to-slate-900"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10">
              <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-sm rounded-full text-purple-300 text-xs font-semibold mb-4 border border-purple-500/30">
                <i className="ri-gift-line mr-1.5 text-sm"></i>
                Earn Up to 500 PEP Per Referral
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent mb-3">
                Referral Rewards
              </h1>
              <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto">
                Build your crypto portfolio by inviting friends. Earn rewards for every successful referral.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-12">
              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-4 sm:p-5 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:transform hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/50">
                      <i className="ri-group-line text-white text-base sm:text-lg"></i>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-400 mb-0.5">Total Referrals</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">{stats.totalReferrals}</p>
                  <p className="text-xs text-slate-500">All time</p>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-4 sm:p-5 border border-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300 hover:transform hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/50">
                      <i className="ri-check-double-line text-white text-base sm:text-lg"></i>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-400 mb-0.5">Active</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">{stats.completedReferrals}</p>
                  <p className="text-xs text-slate-500">Verified</p>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-4 sm:p-5 border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:transform hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/50">
                      <i className="ri-coins-line text-white text-base sm:text-lg"></i>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-400 mb-0.5">Total Earned</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">{stats.totalEarnings.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">PEP ≈ ${(stats.totalEarnings * 0.03).toFixed(2)}</p>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-4 sm:p-5 border border-amber-500/20 hover:border-amber-500/40 transition-all duration-300 hover:transform hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/50">
                      <i className="ri-time-line text-white text-base sm:text-lg"></i>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-400 mb-0.5">Pending</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">{stats.pendingRewards.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Processing</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
              <div className="xl:col-span-2 bg-white/80 backdrop-blur-sm rounded-xl p-4 sm:p-5 shadow-xl border border-white/50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900">Share & Earn</h2>
                  <div className="px-2.5 py-1 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full text-purple-700 text-xs font-semibold self-start sm:self-auto">
                    $6 per referral
                  </div>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
                      Your Referral Code
                    </label>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      <div className="flex-1 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg px-3 py-2.5 min-h-[44px] flex items-center">
                        {referralCode ? (
                          <code className="text-sm sm:text-base font-mono font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent break-all">
                            {referralCode}
                          </code>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin w-3.5 h-3.5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                            <span className="text-slate-500 text-xs">Generating...</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={generateNewCode}
                        disabled={generating}
                        className="flex-1 sm:flex-none px-3 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg hover:from-emerald-700 hover:to-emerald-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-semibold text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-h-[44px]"
                        title="Generate a new referral code"
                      >
                        {generating ? (
                          <>
                            <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full mr-1.5"></div>
                            <span className="hidden sm:inline">Creating...</span>
                            <span className="sm:hidden">New</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-refresh-line mr-1 text-sm"></i>
                            <span className="hidden sm:inline">Create New</span>
                            <span className="sm:hidden">New</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={copyToClipboard}
                        disabled={!referralCode}
                        className="flex-1 sm:flex-none px-3 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-semibold text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
                      >
                        {copied ? (
                          <>
                            <i className="ri-check-line mr-1 text-sm"></i>
                            Copied!
                          </>
                        ) : (
                          <>
                            <i className="ri-file-copy-line mr-1 text-sm"></i>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    {error && (
                      <div className="mt-1.5 flex items-center space-x-1.5 text-red-600 text-xs">
                        <i className="ri-error-warning-line text-sm"></i>
                        <span>{error}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
                      Your Referral Link
                    </label>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2.5 min-h-[44px] flex items-center">
                        <code className="text-xs text-slate-600 break-all leading-relaxed">{referralLink}</code>
                      </div>
                      <button
                        onClick={copyToClipboard}
                        disabled={!referralCode}
                        className="px-3 py-2.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center text-xs sm:text-sm font-semibold"
                      >
                        <i className="ri-file-copy-line mr-1.5 text-sm"></i>
                        <span className="sm:hidden">Copy Link</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Share on Social Media
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                      <button
                        onClick={() => shareOnSocial('twitter')}
                        disabled={!referralCode}
                        className="flex items-center justify-center px-2.5 py-2.5 bg-gradient-to-r from-blue-400 to-blue-500 text-white rounded-lg hover:from-blue-500 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <i className="ri-twitter-fill mr-1.5 text-sm"></i>
                        <span>Twitter</span>
                      </button>
                      <button 
                        onClick={shareOnFacebook}
                        disabled={!referralCode}
                        className="flex items-center justify-center px-2.5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <i className="ri-facebook-fill mr-1.5 text-sm"></i>
                        <span>Facebook</span>
                      </button>
                      <button
                        onClick={() => shareOnSocial('linkedin')}
                        disabled={!referralCode}
                        className="flex items-center justify-center px-2.5 py-2.5 bg-gradient-to-r from-blue-700 to-blue-800 text-white rounded-lg hover:from-blue-800 hover:to-blue-900 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <i className="ri-linkedin-fill mr-1.5 text-sm"></i>
                        <span>LinkedIn</span>
                      </button>

                      <button
                        onClick={shareOnTikTok}
                        disabled={!referralCode}
                        className="flex items-center justify-center px-2.5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <i className="ri-tiktok-fill mr-1.5 text-sm"></i>
                        <span>TikTok</span>
                      </button>

                      <button
                        onClick={() => shareOnSocial('whatsapp')}
                        disabled={!referralCode}
                        className="flex items-center justify-center px-2.5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <i className="ri-whatsapp-fill mr-1.5 text-sm"></i>
                        <span>WhatsApp</span>
                      </button>
                      <button
                        onClick={() => shareOnSocial('telegram')}
                        disabled={!referralCode}
                        className="flex items-center justify-center px-2.5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                      >
                        <i className="ri-telegram-fill mr-1.5 text-sm"></i>
                        <span>Telegram</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 sm:p-5 shadow-xl border border-white/50">
                <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4">How It Works</h2>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start space-x-2.5">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold text-xs sm:text-sm">1</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-0.5 text-xs sm:text-sm">Share Your Link</h3>
                      <p className="text-slate-600 text-xs leading-relaxed">Share your unique referral link with friends through social media or messaging.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2.5">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold text-xs sm:text-sm">2</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-0.5 text-xs sm:text-sm">Friend Signs Up</h3>
                      <p className="text-slate-600 text-xs leading-relaxed">When someone clicks your link and creates an account, they become your referral.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2.5">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold text-xs sm:text-sm">3</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-0.5 text-xs sm:text-sm">Get Notified</h3>
                      <p className="text-slate-600 text-xs leading-relaxed">Receive instant notifications when someone signs up using your referral code.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2.5">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold text-xs sm:text-sm">4</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-0.5 text-xs sm:text-sm">Earn Rewards</h3>
                      <p className="text-slate-600 text-xs leading-relaxed">You earn $6 for each successful referral after they complete their first transaction.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 sm:mt-10">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 sm:p-5 shadow-xl border border-white/50">
                <div className="flex items-center justify-between mb-4 sm:mb-5">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">Your Referrals</h2>
                    <p className="text-slate-600 mt-0.5 text-xs">Track your referral performance</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg sm:text-xl font-bold text-purple-600">{stats.totalReferrals}</div>
                    <div className="text-xs text-slate-500">Total Users</div>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-7 h-7 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-slate-600 mt-3 font-medium text-xs sm:text-sm">Loading referral data...</p>
                  </div>
                ) : referrals.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-2.5 px-2.5 font-bold text-slate-900 text-xs">User</th>
                          <th className="text-left py-2.5 px-2.5 font-bold text-slate-900 text-xs hidden sm:table-cell">Email</th>
                          <th className="text-left py-2.5 px-2.5 font-bold text-slate-900 text-xs">Reward</th>
                          <th className="text-left py-2.5 px-2.5 font-bold text-slate-900 text-xs">Status</th>
                          <th className="text-left py-2.5 px-2.5 font-bold text-slate-900 text-xs hidden md:table-cell">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((referral) => (
                          <tr key={referral.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                            <td className="py-3 px-2.5">
                              <div className="flex items-center space-x-2">
                                <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                                  <span className="text-white font-bold text-xs">
                                    {referral.referred_user.full_name.charAt(0)}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <span className="font-semibold text-slate-900 text-xs block truncate">
                                    {referral.referred_user.full_name}
                                  </span>
                                  <span className="text-slate-600 text-xs sm:hidden block truncate">
                                    {referral.referred_user.email}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-2.5 text-slate-600 font-medium text-xs hidden sm:table-cell">
                              <div className="max-w-[200px] truncate">
                                {referral.referred_user.email}
                              </div>
                            </td>
                            <td className="py-3 px-2.5">
                              <span className="font-bold text-emerald-600 text-xs whitespace-nowrap">
                                ${referral.reward_amount}
                              </span>
                            </td>
                            <td className="py-3 px-2.5">
                              {getStatusBadge(referral.status)}
                            </td>
                            <td className="py-3 px-2.5 text-slate-600 text-xs font-medium hidden md:table-cell">
                              {formatDate(referral.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <i className="ri-group-line text-slate-400 text-2xl"></i>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1.5">No referrals yet</h3>
                    <p className="text-slate-600 mb-5 max-w-md mx-auto text-xs">
                      Start sharing your referral link to invite friends and earn $6 per successful referral.
                    </p>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-semibold text-xs sm:text-sm"
                    >
                      <i className="ri-share-line mr-1.5"></i>
                      Share Your Link Now
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6 mb-6 sm:mb-8 border-2 border-orange-200 mt-6 sm:mt-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-5 gap-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-3">
                    <i className="ri-search-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-gray-900">Lost Referrals Recovery</h2>
                    <p className="text-gray-600 text-xs mt-0.5">Search ALL registered users and claim missing referrals</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLostReferralsModal(true)}
                  className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-semibold text-xs sm:text-sm min-h-[44px]"
                >
                  <i className="ri-search-2-line mr-1.5 text-sm"></i>
                  Search All Users
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <i className="ri-database-2-line text-2xl text-orange-600"></i>
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">COMPREHENSIVE</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-0.5">Full Database Search</h3>
                  <p className="text-xs text-gray-600">Search through ALL registered users in both profiles and auth tables</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <i className="ri-shield-check-line text-2xl text-blue-600"></i>
                    <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">SMART</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-0.5">Intelligent Matching</h3>
                  <p className="text-xs text-gray-600">Exclude already claimed referrals and prioritize exact matches</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <i className="ri-gift-line text-2xl text-green-600"></i>
                    <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">INSTANT</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-0.5">Immediate Rewards</h3>
                  <p className="text-xs text-gray-600">Get $6 instantly credited after referee's first transaction</p>
                </div>
              </div>

              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start">
                  <i className="ri-information-line text-yellow-600 text-lg mr-2 mt-0.5"></i>
                  <div>
                    <h4 className="font-semibold text-yellow-900 mb-0.5 text-xs">Enhanced Search Features</h4>
                    <ul className="text-xs text-yellow-800 space-y-0.5">
                      <li>• Search by email or partial email (e.g., "john" finds john@example.com)</li>
                      <li>• Searches both profiles table AND auth.users table for complete coverage</li>
                      <li>• Automatically excludes users you've already referred</li>
                      <li>• Smart sorting: exact matches first, then partial matches</li>
                      <li>• Up to 15 results per search with relevance ranking</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {showLostReferralsModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 text-white p-5 sm:p-6 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-3">
                          <i className="ri-search-2-line text-lg sm:text-xl"></i>
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold">Search All Registered Users</h3>
                          <p className="text-orange-100 text-xs mt-0.5">Comprehensive database search for lost referrals</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowLostReferralsModal(false);
                          setLostReferrals([]);
                          setSearchEmail('');
                        }}
                        className="w-9 h-9 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-all cursor-pointer"
                      >
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                  </div>

                  <div className="p-4 sm:p-5">
                    <div className="mb-5">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                        Enter Email Address or Name
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchEmail}
                          onChange={(e) => setSearchEmail(e.target.value)}
                          placeholder="example@email.com or john"
                          className="flex-1 px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none text-xs sm:text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && searchLostReferrals()}
                        />
                        <button
                          onClick={searchLostReferrals}
                          disabled={searchLoading}
                          className="px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-300 font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-xs flex-shrink-0 min-h-[44px]"
                        >
                          {searchLoading ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-1.5"></i>
                              <span className="hidden sm:inline">Searching...</span>
                              <span className="sm:hidden">...</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-search-line mr-1.5"></i>
                              <span className="hidden sm:inline">Search All</span>
                              <span className="sm:hidden">Go</span>
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        <i className="ri-information-line mr-1"></i>
                        Searches ALL users in profiles AND auth tables. Use partial terms like "john" to find john@example.com
                      </p>
                    </div>

                    {lostReferrals.length > 0 && (
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center text-sm">
                          <i className="ri-user-search-line text-orange-600 mr-1.5"></i>
                          Found {lostReferrals.length} Potential Referral{lostReferrals.length !== 1 ? 's' : ''}
                        </h4>
                        <div className="space-y-2.5">
                          {lostReferrals.map((referral) => (
                            <div
                              key={referral.id}
                              className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-3"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center flex-1 min-w-0">
                                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-2.5 flex-shrink-0">
                                    {referral.full_name?.charAt(0) || referral.email?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-bold text-gray-900 text-xs sm:text-sm truncate">
                                      {referral.full_name || 'User'}
                                    </h5>
                                    <p className="text-xs text-gray-600 truncate">{referral.email}</p>
                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                      <p className="text-xs text-gray-500">
                                        <i className="ri-calendar-line mr-0.5"></i>
                                        {new Date(referral.created_at).toLocaleDateString()}
                                      </p>
                                      {referral.source && (
                                        <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">
                                          {referral.source === 'auth' ? 'Auth Table' : 'Profile'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleClaimLostReferral(referral.id)}
                                  disabled={isClaimingReferral === referral.id}
                                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-xs flex-shrink-0 min-h-[40px]"
                                >
                                  {isClaimingReferral === referral.id ? (
                                    <>
                                      <i className="ri-loader-4-line animate-spin mr-1"></i>
                                      <span className="hidden sm:inline">Claiming...</span>
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-gift-line mr-1"></i>
                                      <span className="hidden sm:inline">Claim $6</span>
                                      <span className="sm:hidden">Claim</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {searchEmail && lostReferrals.length === 0 && !searchLoading && (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <i className="ri-search-line text-3xl text-gray-400"></i>
                        </div>
                        <p className="text-gray-600 font-semibold mb-1.5 text-sm">No Potential Referrals Found</p>
                        <p className="text-xs text-gray-500">
                          No users match your search or all found users are already claimed
                        </p>
                      </div>
                    )}

                    {!searchEmail && lostReferrals.length === 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start">
                          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <i className="ri-lightbulb-line text-white text-lg"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-blue-900 mb-1.5 text-sm">Comprehensive Search System</h4>
                            <ol className="text-xs text-blue-800 space-y-1.5">
                              <li className="flex items-start">
                                <span className="font-bold mr-1.5">1.</span>
                                <span>Enter email or name (partial matches work: "john" finds john@example.com)</span>
                              </li>
                              <li className="flex items-start">
                                <span className="font-bold mr-1.5">2.</span>
                                <span>System searches ALL registered users in both profiles and auth tables</span>
                              </li>
                              <li className="flex items-start">
                                <span className="font-bold mr-1.5">3.</span>
                                <span>Automatically excludes users you've already referred</span>
                              </li>
                              <li className="flex items-start">
                                <span className="font-bold mr-1.5">4.</span>
                                <span>Click "Claim $6" to add them as referral. You'll receive $6 after their first transaction.</span>
                              </li>
                            </ol>
                            <div className="mt-3 p-2.5 bg-blue-100 rounded-lg">
                              <p className="text-xs text-blue-700 font-semibold">
                                <i className="ri-star-line mr-1"></i>
                                Enhanced Features: Smart relevance sorting, up to 15 results, real-time duplicate filtering
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Search Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <i className="ri-search-line text-white text-xl"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Search & Claim Referrals</h2>
              </div>

              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                {isSearching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 mb-3">
                    Found {searchResults.length} user{searchResults.length !== 1 ? 's' : ''}
                  </p>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {searchResults.map((result) => (
                      <div
                        key={result.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{result.full_name}</p>
                          <p className="text-sm text-gray-600">{result.email}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Joined: {new Date(result.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {result.isReferred ? (
                            <span className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg text-sm font-medium whitespace-nowrap">
                              Already Referred
                            </span>
                          ) : (
                            <button
                              onClick={() => handleClaimReferral(result.id)}
                              disabled={claimingUserId === result.id}
                              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap flex items-center gap-2"
                            >
                              {claimingUserId === result.id ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Claiming...
                                </>
                              ) : (
                                <>
                                  <i className="ri-gift-line"></i>
                                  Claim $6
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && !isSearching && searchResults.length === 0 && (
                <div className="text-center py-8">
                  <i className="ri-user-search-line text-4xl text-gray-300 mb-2"></i>
                  <p className="text-gray-500">No users found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
